import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../../lib/supabase";
import { X, Send, User, MessageCircle, AlertCircle, Clock } from "lucide-react";
import { toast } from "react-toastify";

const QAThread = ({ questionId, onClose, currentUser, onViewProfile }) => {
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAnswer, setNewAnswer] = useState("");
  const [replyTo, setReplyTo] = useState(null); // { id, author_alias, content }
  const [isPosting, setIsPosting] = useState(false);
  const inputRef = useRef(null);

  // PWA UX: Prevent pull-to-refresh when modal is open
  useEffect(() => {
    document.body.classList.add("no-pull-refresh");
    return () => document.body.classList.remove("no-pull-refresh");
  }, []);

  // Swipe to close logic for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 150;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isDownwardSwipe = distance > minSwipeDistance;
    if (isDownwardSwipe) {
      onClose();
    }
  };

  useEffect(() => {
    fetchData();
    
    // Subscribe to new answers (comments)
    const channel = supabase
      .channel(`qa_answers:${questionId}`)
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'comments', 
        filter: `post_id=eq.${questionId}` 
      }, (payload) => {
        handleRealtimeAnswer(payload.new);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [questionId]);

  const fetchData = async () => {
    setLoading(true);
    
    // Fetch Question details
    const { data: qData } = await supabase
      .from("posts")
      .select("*, profiles:author_id(avatar_url, campus_code)")
      .eq("id", questionId)
      .single();
    
    setQuestion(qData);

    // Fetch Answers (Comments)
    const { data: aData } = await supabase
      .from("comments")
      .select("*, profiles:user_id(campus_alias, avatar_url, campus_code)")
      .eq("post_id", questionId)
      .order("created_at", { ascending: true });

    setAnswers(aData || []);
    setLoading(false);
  };

  const handleRealtimeAnswer = async (newRecord) => {
    const { data } = await supabase
      .from("profiles")
      .select("campus_alias, avatar_url, campus_code")
      .eq("id", newRecord.user_id)
      .single();

    const answerWithProfile = { ...newRecord, profiles: data };
    setAnswers(prev => {
        // Handle deduplication for optimistic updates if needed, though simple realtime is fine here
        if (prev.find(a => a.id === newRecord.id)) return prev;
        return [...prev, answerWithProfile];
    });
  };

  const handleReply = (ans) => {
    setReplyTo({
        id: ans.id,
        author_alias: ans.profiles?.campus_alias || "User",
        content: ans.content
    });
    inputRef.current?.focus();
  };

  const handlePostAnswer = async () => {
    if (!newAnswer.trim() || isPosting) return;

    const content = newAnswer.trim();
    setNewAnswer("");
    setIsPosting(true);

    const { error } = await supabase
      .from("comments")
      .insert([{ 
        post_id: questionId, 
        user_id: currentUser.id, 
        content: content,
        parent_id: replyTo ? replyTo.id : null
      }]);

    if (!error) {
        setReplyTo(null);
    }

    if (error) {
      toast.error("Failed to post answer.");
      setNewAnswer(content);
    }
    setIsPosting(false);
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-500">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={onClose}></div>

      <div 
        className="relative bg-white dark:bg-[#050507] w-full max-w-2xl h-[95vh] sm:h-auto sm:max-h-[85vh] sm:rounded-[2.5rem] rounded-t-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-full duration-700 transition-all border border-slate-100 dark:border-white/5"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        
        {/* Header - More appropriate for mobile */}
        <div className="flex flex-col items-center pt-2 pb-0 sm:hidden">
          <div className="w-10 h-1 bg-slate-200 dark:bg-indigo-500/20 rounded-full mb-1"></div>
        </div>

        <div className="p-5 sm:p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-white/80 dark:bg-[#050507]/80 backdrop-blur-2xl sticky top-0 z-20">
            <div className="flex items-center gap-4">
                <div className="p-2.5 sm:p-3 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl">
                    <MessageCircle size={20} />
                </div>
                <div>
                    <h3 className="font-black text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight">Question</h3>
                    <p className="text-[9px] sm:text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">Details & Answers</p>
                </div>
            </div>
            <button onClick={onClose} className="p-3 hover:bg-slate-100 dark:hover:bg-white/10 rounded-2xl transition-all text-slate-400" aria-label="Close">
                <X size={22} className="sm:size-5" />
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-10 bg-transparent custom-scrollbar">
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 opacity-40">
                    <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-6" />
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Loading...</p>
                </div>
            ) : (
                <>
                    {/* The Question - Premium Elevation */}
                    <div className="glass-card p-8 border border-indigo-100/50 dark:border-indigo-900/30 shadow-xl shadow-indigo-500/5">
                        <div className="flex items-center gap-4 mb-6">
                            <button
                              onClick={() => onViewProfile?.(question.author_id)}
                              className="shrink-0 hover:opacity-80 transition-opacity"
                            >
                              {question.profiles?.avatar_url ? (
                                <img
                                  src={question.profiles.avatar_url}
                                  alt={question.author_alias}
                                  className="w-12 h-12 rounded-2xl object-cover border-2 border-white dark:border-slate-800 shadow-sm"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-extrabold text-xl border-2 border-white dark:border-slate-800 shadow-sm">
                                  {question?.author_alias?.[0]}
                                </div>
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <button
                                      onClick={() => onViewProfile?.(question.author_id)}
                                      className="font-black text-slate-900 dark:text-white leading-tight truncate hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                    >
                                      {question?.author_alias}
                                    </button>
                                    {question?.campus_code && (
                                        <span className={`text-[8px] px-2 py-0.5 rounded-lg font-black uppercase tracking-widest ${
                                            question.campus_code === currentUser.campus_code 
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-500"
                                        }`}>
                                            {question.campus_code}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest">{question?.category}</span>
                                    <div className="w-1 h-1 rounded-full bg-slate-200 dark:bg-slate-800"></div>
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">{new Date(question?.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white leading-snug">
                            {question?.content}
                        </h2>
                    </div>

                    {/* Answers Section */}
                    <div className="space-y-6 pb-6">
                        <div className="flex items-center gap-4 px-2">
                            <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] shrink-0">
                                {answers.length} {answers.length === 1 ? 'Answer' : 'Answers'}
                            </h3>
                            <div className="flex-1 h-[1px] bg-slate-100 dark:bg-white/5"></div>
                        </div>

                        {answers.length === 0 ? (
                            <div className="py-12 text-center glass-card border-dashed">
                                <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-white/5 flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle size={24} className="text-slate-300" />
                                </div>
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400">Awaiting your insight.</p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {answers.filter(a => !a.parent_id).map((ans) => {
                                    const replies = answers.filter(r => r.parent_id === ans.id);
                                    return (
                                        <div key={ans.id} className="space-y-6">
                                            {/* Root Answer */}
                                            <div className="flex gap-4 group animate-in fade-in slide-in-from-left-4 duration-500">
                                                <div className="shrink-0 pt-1">
                                                    <button
                                                      onClick={() => onViewProfile?.(ans.user_id)}
                                                      className="hover:opacity-80 transition-opacity"
                                                    >
                                                      {ans.profiles?.avatar_url ? (
                                                        <img
                                                          src={ans.profiles.avatar_url}
                                                          alt={ans.profiles?.campus_alias}
                                                          className="w-10 h-10 rounded-xl object-cover border border-slate-100 dark:border-white/5 group-hover:border-indigo-400 transition-all"
                                                        />
                                                      ) : (
                                                        <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-white/5 flex items-center justify-center text-slate-400 dark:text-slate-400 font-black text-sm border border-slate-100 dark:border-white/5 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/40 group-hover:text-indigo-600 transition-all">
                                                          {ans.profiles?.campus_alias?.[0]}
                                                        </div>
                                                      )}
                                                    </button>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="glass-card shadow-sm p-5 border border-slate-100 dark:border-white/5 group-hover:border-indigo-100/50 dark:group-hover:border-indigo-900/30 transition-all">
                                                        <div className="flex items-center justify-between mb-3 gap-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest truncate">{ans.profiles?.campus_alias}</span>
                                                                {ans.profiles?.campus_code && (
                                                                    <span className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase tracking-widest ${
                                                                        ans.profiles.campus_code === currentUser.campus_code 
                                                                        ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                                                                        : "bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-500"
                                                                    }`}>
                                                                        {ans.profiles.campus_code}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-[9px] text-slate-400 font-bold shrink-0">{getTimeAgo(ans.created_at)}</span>
                                                        </div>
                                                        <p className="text-[14px] text-slate-800 dark:text-slate-200 leading-relaxed font-medium whitespace-pre-wrap">{ans.content}</p>
                                                    </div>
                                                    <div className="mt-2 ml-1">
                                                        <button 
                                                            onClick={() => handleReply(ans)}
                                                            className="text-[10px] font-black text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 uppercase tracking-widest transition-colors"
                                                        >
                                                            Reply
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Nested Replies */}
                                            {replies.map(reply => (
                                                <div key={reply.id} className="flex gap-4 ml-12 group animate-in fade-in slide-in-from-left-2 duration-500">
                                                    <div className="shrink-0 pt-1">
                                                        <button
                                                          onClick={() => onViewProfile?.(reply.user_id)}
                                                          className="hover:opacity-80 transition-opacity"
                                                        >
                                                          {reply.profiles?.avatar_url ? (
                                                            <img
                                                              src={reply.profiles.avatar_url}
                                                              alt={reply.profiles?.campus_alias}
                                                              className="w-8 h-8 rounded-lg object-cover border border-slate-100 dark:border-white/5"
                                                            />
                                                          ) : (
                                                            <div className="w-8 h-8 rounded-lg bg-slate-50/50 dark:bg-white/5 flex items-center justify-center text-slate-400 font-black text-[10px] border border-slate-100 dark:border-white/5">
                                                              {reply.profiles?.campus_alias?.[0]}
                                                            </div>
                                                          )}
                                                        </button>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="bg-slate-50/50 dark:bg-white/5 p-4 rounded-[1.2rem] border border-slate-100 dark:border-white/5 group-hover:border-slate-200 dark:group-hover:border-white/10 transition-all shadow-sm">
                                                            <div className="flex items-center justify-between mb-2 gap-2">
                                                                <div className="flex items-center gap-2 overflow-hidden">
                                                                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest truncate">{reply.profiles?.campus_alias}</span>
                                                                    {reply.profiles?.campus_code && (
                                                                        <span className={`text-[7px] px-1 py-0.5 rounded font-black uppercase tracking-widest ${
                                                                            reply.profiles.campus_code === currentUser.campus_code 
                                                                            ? "text-indigo-600 dark:text-indigo-400"
                                                                            : "text-slate-400"
                                                                        }`}>
                                                                            {reply.profiles.campus_code}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="text-[9px] text-slate-400 font-bold shrink-0">{getTimeAgo(reply.created_at)}</span>
                                                            </div>
                                                            <p className="text-[13px] text-slate-700 dark:text-slate-300 leading-relaxed font-medium whitespace-pre-wrap">{reply.content}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>

        {/* Footer Area - Input */}
        <div className="p-8 bg-white dark:bg-[#050507] border-t border-slate-100 dark:border-white/5">
            {replyTo && (
                <div className="flex items-center justify-between px-5 py-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl border border-indigo-100/50 dark:border-indigo-800/50 mb-4 animate-in slide-in-from-bottom-2 duration-300">
                    <span className="text-[10px] font-black text-indigo-700 dark:text-indigo-300 uppercase tracking-widest">
                        Extending {replyTo.author_alias}'s context
                    </span>
                    <button onClick={() => setReplyTo(null)} className="text-indigo-400 hover:text-indigo-600 transition-colors">
                        <X size={16} />
                    </button>
                </div>
            )}
            <div className={`flex items-end gap-4 bg-slate-50 dark:bg-slate-900 p-3 rounded-[1.5rem] border border-transparent focus-within:ring-4 focus-within:ring-indigo-500/5 shadow-sm transition-all ${replyTo ? 'border-indigo-100 dark:border-indigo-900/50' : ''}`}>
                <textarea
                    ref={inputRef}
                    value={newAnswer}
                    onChange={(e) => setNewAnswer(e.target.value)}
                    placeholder={replyTo ? `Reply to ${replyTo.author_alias}...` : "Write your answer..."}
                    className="flex-1 bg-transparent border-none focus:ring-0 py-3 px-3 text-[14px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 resize-none max-h-32"
                    rows={1}
                />
                <button
                    onClick={handlePostAnswer}
                    disabled={!newAnswer.trim() || isPosting}
                    className="w-12 h-12 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-indigo-200 dark:shadow-none flex items-center justify-center shrink-0 active:scale-95 mb-1"
                >
                    {isPosting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={20} />}
                </button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default QAThread;
